import { useEffect } from 'react';
import Delta from 'quill-delta';
import { Nullable } from 'global';
import { formatCurrency } from 'utils';
import { useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { ProposalMenuOptionType } from '@trnsact/trnsact-shared-types';
import { deskingSelectors } from '../model';
import { BuilderViewType, FinanceProgramFormValues, MarkupType } from '../types';
import { capitalize } from '@mui/material';

export function useFPDGenerator(isGenActive: boolean) {
  const menuBuilderTerm = useSelector(deskingSelectors.term);
  const menuByTerm = useSelector(deskingSelectors.menuByTerm);
  const currentTerm = useSelector(deskingSelectors.getSelectedFinanceOptionTerm);
  const financePrising = useSelector(deskingSelectors.financePrising);
  const menuBuilderViewType = useSelector(deskingSelectors.menuBuilderViewType);
  const menuPricing = useSelector(deskingSelectors.menuPricing);
  const selectedMenuOption = useSelector(deskingSelectors.selectedMenuOption);

  const { control, setValue, getValues } = useFormContext<FinanceProgramFormValues>();
  const watchedValues = useWatch({
    control,
    name: ['financeQuote.programType', 'financeQuote.daysToFirstPayment', 'financeQuote.fee'],
  });

  useEffect(() => {
    if (isGenActive) {
      const generateFPDBuilders: Record<BuilderViewType, Nullable<() => Delta>> = {
        [BuilderViewType.Finance]: () => {
          if (!financePrising[currentTerm]) return new Delta();

          const [programType, daysToFirstPayment, fees] = watchedValues;

          const { term, amountDueToday, rate, customerRate } = financePrising[currentTerm];

          const quillDeltaFPD = new Delta()
            .insert(programType, { bold: true })
            .insert(' | ')
            .insert(`${term} Months`)
            .insert(' | ')
            .insert(`${customerRate || rate}%`, { italic: true })
            .insert(' | ')
            .insert(`Deferred Payments: ${daysToFirstPayment ?? 0} Days to 1st Month's payment`)
            .insert('\n')
            .insert('Amount due in advance: ', { bold: true })
            .insert(formatCurrency(amountDueToday, true))
            .insert('\n');

          if (fees.length) {
            quillDeltaFPD.insert('Fees', { bold: true }).insert('\n');

            fees.forEach(({ identifier, fee, type }) => {
              quillDeltaFPD
                .insert(identifier ? `${identifier}: ` : '')
                .insert(type === MarkupType.Flat ? formatCurrency(fee, true) : `${fee}%`)
                .insert('\n', { list: 'bullet' });
            });
          }

          return quillDeltaFPD;
        },
        [BuilderViewType.AftermarketMenu]: () => {
          if (!menuPricing[menuBuilderTerm.term]) return new Delta();

          const [programType, daysToFirstPayment, fees] = watchedValues;

          const [payment] = Object.values(menuPricing[menuBuilderTerm.term]).toSorted(
            (a, b) => a.paymentAmountPerPeriod - b.paymentAmountPerPeriod
          );

          const { term, amountDueToday, rate, customerRate, paymentAmountPerPeriod } = payment;

          const isMenuHasDeclinationOption = (menuByTerm ?? []).some(
            ({ type }) => type === ProposalMenuOptionType.Declination
          );

          const builder = new Delta().insert(programType, { bold: true }).insert(' | ').insert(`${term} Months`);

          if (selectedMenuOption) {
            builder.insert(' - ').insert(`${capitalize(selectedMenuOption.name)} option`);
          }

          if (isMenuHasDeclinationOption) {
            builder
              .insert(' | ')
              .insert(`Base payment ${formatCurrency(paymentAmountPerPeriod, true)}`)
              .insert(' | ');
          }

          builder
            .insert(`${customerRate ?? rate}%`, { italic: true })
            .insert(' | ')
            .insert(`Deferred Payments: ${daysToFirstPayment ?? 0} Days to 1st Month's payment`)
            .insert('\n')
            .insert('Amount due in advance: ', { bold: true })
            .insert(formatCurrency(amountDueToday, true))
            .insert('\n');

          if (fees.length) {
            builder.insert('Fees', { bold: true }).insert('\n');

            fees.forEach(({ identifier, fee, type }) => {
              builder
                .insert(identifier ? `${identifier}: ` : '')
                .insert(type === MarkupType.Flat ? formatCurrency(fee, true) : `${fee}%`)
                .insert('\n', { list: 'bullet' });
            });
          }

          return builder;
        },
      };

      if (typeof generateFPDBuilders[menuBuilderViewType] === 'function') {
        const fpd = generateFPDBuilders[menuBuilderViewType]();

        const converter = new QuillDeltaToHtmlConverter(fpd.ops, {});
        const html = converter.convert();

        setValue('financeProgramDescription.editorValue', fpd as any);
        setValue('financeProgramDescription.apiValue', html);
      }
    }
  }, [
    setValue,
    getValues,
    menuByTerm,
    menuPricing,
    currentTerm,
    isGenActive,
    watchedValues,
    financePrising,
    menuBuilderTerm,
    selectedMenuOption,
    menuBuilderViewType,
  ]);
}
